import React from 'react';
import {
    Box,
    Typography,
    Drawer,
    IconButton,
    Card,
    Button,
    alpha,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Stack,
    Chip,
} from '@mui/material';
import {
    Close as CloseIcon,
    AutoAwesome as MagicIcon,
    Check as CheckIcon,
    EmojiObjects as IdeaIcon,
} from '@mui/icons-material';

const SuggestionPanel = ({
    open,
    onClose,
    loading,
    suggestions = [],
    onApply,
    platform
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const renderContent = () => (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: theme.palette.mode === 'dark' ? alpha('#000', 0.8) : alpha('#fff', 0.8),
            backdropFilter: 'blur(20px)',
        }}>
            {/* Header */}
            <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <MagicIcon sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>AI Content Wizard</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Body */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                        <CircularProgress size={32} thickness={5} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                            Optimizing for {platform}...
                        </Typography>
                    </Box>
                ) : suggestions.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <IdeaIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
                        <Typography variant="body2" sx={{ color: 'text.secondary', px: 4 }}>
                            No suggestions yet. Click the magic wand to optimize your draft!
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={3}>
                        {suggestions.map((s, i) => (
                            <Card
                                key={i}
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 4,
                                    border: `1px solid ${theme.palette.divider}`,
                                    bgcolor: alpha(theme.palette.background.paper, 0.4),
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.1)}`,
                                    }
                                }}
                            >
                                {s.title && (
                                    <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1, color: 'primary.main' }}>
                                        {s.title}
                                    </Typography>
                                )}

                                <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                                    {s.text}
                                </Typography>

                                {s.hook && (
                                    <Box sx={{ mb: 2, p: 1.5, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: `1px dashed ${alpha(theme.palette.success.main, 0.2)}` }}>
                                        <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 700, display: 'block', mb: 0.5 }}>
                                            STRATEGY:
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                                            {s.hook}
                                        </Typography>
                                    </Box>
                                )}

                                {s.hashtags && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2.5 }}>
                                        {Array.isArray(s.hashtags) ? s.hashtags.map((h, j) => (
                                            <Chip key={j} label={h} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                        )) : (
                                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>{s.hashtags}</Typography>
                                        )}
                                    </Box>
                                )}

                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="small"
                                    startIcon={<CheckIcon />}
                                    onClick={() => onApply(s)}
                                    data-testid={`apply-suggestion-${i}`}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        boxShadow: 'none',
                                        '&:hover': { boxShadow: 'none' }
                                    }}
                                >
                                    Apply this version
                                </Button>
                            </Card>
                        ))}
                    </Stack>
                )}
            </Box>

            {/* Footer */}
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                    AI-generated content. Please review before publishing.
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Drawer
            anchor={isMobile ? 'bottom' : 'right'}
            open={open}
            onClose={onClose}
            variant="temporary"
            PaperProps={{
                sx: {
                    width: isMobile ? '100%' : 400,
                    height: isMobile ? '80vh' : '100%',
                    borderTopLeftRadius: isMobile ? 24 : 0,
                    borderBottomLeftRadius: isMobile ? 0 : 24,
                    boxShadow: '-10px 0 40px rgba(0,0,0,0.1)',
                    backgroundImage: 'none',
                }
            }}
        >
            {renderContent()}
        </Drawer>
    );
};

export default SuggestionPanel;
